import { useCallback, useEffect, useState } from "react";
import { PiggyBank, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { money, monthLabel } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { Field, Loader, PageHeader } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import { slug } from "@/lib/constants";
import { writesLocked } from "@/lib/splits";

const GREEN = "var(--glow-money)";
const BAR = { green: "var(--st-green)", amber: "var(--st-amber4)", red: "var(--st-red)" };

function BudgetDialog({ hubId, cat, onClose, onSaved }) {
  const [amount, setAmount] = useState(cat.budget ? String(cat.budget) : "");
  const [err, setErr] = useState(null);
  const save = (e) => {
    e.preventDefault();
    api.put(`/organizations/${hubId}/budgets`, { category: cat.category, amount }).then(() => { toast.success(`${cat.category} budget saved`); onSaved(); })
      .catch((x) => setErr(fieldErrs(x)?.amount || errMsg(x)));
  };
  return (
    <FormDialog open onClose={onClose} title={`${cat.category} budget`} description="Monthly. It carries over to each new month until you change it." testid="budget-dialog">
      <form onSubmit={save} className="space-y-3">
        <Field label="Amount per month" error={err} errorTestid="budget-amount-error">
          <input className="q-input min-h-[44px] w-full" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setErr(null); }} placeholder="250" autoFocus data-testid="budget-amount-input" />
        </Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" data-testid="budget-save-btn">Save budget</button>
      </form>
    </FormDialog>
  );
}

function Row({ r, editable, onEdit }) {
  const rgb = BAR[r.color];
  return (
    <li className="q-card p-4" data-testid={`budget-row-${slug(r.category)}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="font-semibold text-q-text">{r.category}</span>
        <span className="text-sm tabular-nums text-q-muted" data-testid={`budget-figures-${slug(r.category)}`}>{money(r.spent)} of {money(r.budget)} · {r.pct}%</span>
      </div>
      <div className="mt-2 h-3 overflow-hidden rounded-full bg-q-raised" role="progressbar" aria-valuenow={r.pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${r.category} ${r.pct}% used`}>
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(r.pct, 100)}%`, background: `rgb(${rgb})` }} data-testid={`budget-bar-${slug(r.category)}`} data-color={r.color} />
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        {r.over_by > 0 || r.color === "red"
          ? <span className="font-semibold" style={{ color: `rgb(${BAR.red})` }} data-testid={`budget-over-${slug(r.category)}`}>Over by {money(r.over_by)}</span>
          : <span className="text-q-muted" data-testid={`budget-left-${slug(r.category)}`}>{money(r.left)} left</span>}
        {editable && <button className="q-btn q-btn-ghost min-h-[44px]" onClick={onEdit} data-testid={`budget-edit-${slug(r.category)}`}>Edit</button>}
      </div>
    </li>
  );
}

export default function Budgets() {
  const { hub } = useHub();
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/budgets`, { params: month ? { month } : {} }).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id, month]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => ["spending", "bill", "budget"].includes(m.entity), 30000);
  if (!d) return <Loader />;
  const editable = d.editable && !writesLocked(hub);
  return (
    <div data-testid="budgets-page">
      <PageHeader eyebrow="Money" title="Budgets" rgb={GREEN} testid="budgets-title" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <select className="q-input min-h-[44px] w-auto" value={d.month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" data-testid="budgets-month-picker">
          {d.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        {d.month !== d.current_month && <span className="text-sm text-q-muted" data-testid="budgets-past-note">Past month: read-only</span>}
        {d.month === d.current_month && !hub.is_parent && <span className="text-sm text-q-muted" data-testid="budgets-readonly-note">Only parents can change budgets</span>}
      </div>
      {d.budgets.length > 0 && (
        <section className="q-card mb-5 flex flex-wrap gap-x-8 gap-y-2 p-4" data-testid="budgets-totals">
          <div><p className="q-label">Total budget</p><p className="font-display text-2xl font-extrabold text-q-text" data-testid="budgets-total-budget">{money(d.totals.budget)}</p></div>
          <div><p className="q-label">Spent</p><p className="font-display text-2xl font-extrabold text-q-text" data-testid="budgets-total-spent">{money(d.totals.spent)}</p></div>
          <div><p className="q-label">Left</p><p className="font-display text-2xl font-extrabold" style={{ color: `rgb(${d.totals.left < 0 ? BAR.red : GREEN})` }} data-testid="budgets-total-left">{money(d.totals.left)}</p></div>
          <div><p className="q-label">Used</p><p className="font-display text-2xl font-extrabold text-q-text" data-testid="budgets-total-pct">{d.totals.pct}%</p></div>
        </section>
      )}
      {!d.budgets.length && <p className="q-card mb-5 p-4 text-q-muted" data-testid="budgets-empty"><PiggyBank className="mr-2 inline h-4 w-4" />No budgets {d.month === d.current_month ? "yet. Set one for a category below." : "that month."}</p>}
      <ul className="mb-8 space-y-3" data-testid="budgets-list">{d.budgets.map((r) => <Row key={r.category} r={r} editable={editable} onEdit={() => setEdit(r)} />)}</ul>
      <h2 className="mb-3 font-display text-lg font-bold text-q-text">No budget yet</h2>
      <ul className="q-card divide-y divide-q-line p-0" data-testid="no-budget-list">
        {d.no_budget.map((c) => (
          <li key={c.category} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2" data-testid={`no-budget-${slug(c.category)}`}>
            <span className="text-q-text">{c.category} <span className="text-sm text-q-muted">· {money(c.spent)} spent</span></span>
            {editable && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setEdit(c)} data-testid={`set-budget-${slug(c.category)}`}><Plus className="h-4 w-4" /> Set budget</button>}
          </li>
        ))}
      </ul>
      {edit && <BudgetDialog hubId={hub.id} cat={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
